import { CapacitorHttp } from '@capacitor/core';
import { Filesystem, Encoding } from '@capacitor/filesystem';
import { VDPLAYER_BASE_URL } from '@/services/offline/vdplayerBaseUrl';
import { STORAGE_DIR, destDirFor, ensureDir } from '@/services/offline/offlinePaths';

// Legendas CC (WebVTT) de uma aula, para reprodução da fonte local. As legendas
// NÃO vivem no manifest HLS desta plataforma: são sidecar servido pela API do
// vdplayer (`GET broadcasts/{id}/info` → `legendas[]`, cada uma com a URL do
// .vtt em `srT_WebVTT`). O bundle coremedia as anexa via addRemoteTextTrack —
// online ele mesmo chama o /info; com fonte local quem fornece é o app, via
// prop `localSubtitles`, e os .vtt precisam estar no disco.
//
// CapacitorHttp (não axios) pelo mesmo motivo do transcriptService: o JWT do
// EAD gravado em axios.defaults sai concatenado no header e o vdplayer devolve
// 401 silencioso.

// Retorna `legendas[]` (possivelmente vazio = aula sem legendas) ou null em
// QUALQUER falha — o caller distingue "sem legendas" de "não consegui saber"
// (só o primeiro é gravado no store; o segundo fica para o heal retentar).
export async function fetchSubtitleList (broadcastId, token, customerId) {
  if (!broadcastId) return null;
  try {
    const response = await CapacitorHttp.get({
      url: `${VDPLAYER_BASE_URL}broadcasts/${broadcastId}/info`,
      headers: { Authorization: token, CustomerId: String(customerId) },
      connectTimeout: 15000,
      readTimeout: 15000
    });
    if (!response || response.status < 200 || response.status >= 300) return null;
    let data = response.data;
    if (typeof data === 'string') {
      try { data = JSON.parse(data); } catch { return null; }
    }
    if (!data) return null;
    return Array.isArray(data.legendas) ? data.legendas : [];
  } catch {
    return null;
  }
}

// Baixa cada .vtt para `offline/<loId>/sub_<sigla>.vtt` e retorna o índice
// [{sigla, nome, filename}] SÓ dos que gravaram com sucesso.
//
// - Top-level do diretório do LO de propósito: o rmdir recursivo da remoção
//   limpa de graça e o computeOfflineSize (não-recursivo) soma os bytes.
// - Prefixo `sub_` não colide com o namespace `seg_N` dos segmentos HLS.
// - PLAINTEXT de propósito: o video.js busca a track por XHR comum — um .vtt
//   cifrado chegaria como binário ilegível no parser (mesma razão do
//   transcript.json). Por isso Filesystem.writeFile, fora do batch nativo.
// - Fora do batch também por contrato: o awaitBatch não tem item opcional, e
//   um 404 de legenda não pode marcar como falho um download cujo vídeo
//   baixou 100%.
export async function downloadSubtitleFiles (loId, legendas) {
  const saved = [];
  if (!Array.isArray(legendas) || !legendas.length) return saved;

  const dir = destDirFor(loId);
  await ensureDir(dir);

  const usedNames = new Set();
  for (let i = 0; i < legendas.length; i++) {
    const legenda = legendas[i] || {};
    const url = legenda.srT_WebVTT;
    if (!url) continue;

    const siglaSafe = String(legenda.sigla || '').replace(/[^A-Za-z0-9_-]/g, '_');
    let filename = `sub_${siglaSafe || i}.vtt`;
    // Sigla duplicada (ou vazia repetida): sufixa pra não sobrescrever.
    if (usedNames.has(filename)) filename = `sub_${siglaSafe || 'x'}_${i}.vtt`;

    try {
      const response = await CapacitorHttp.get({
        url,
        connectTimeout: 15000,
        readTimeout: 20000
      });
      if (!response || response.status < 200 || response.status >= 300) continue;
      const body = typeof response.data === 'string' ? response.data : null;
      // Portal cativo/erro de CDN devolve HTML com status 200 — só gravamos o
      // que é WebVTT de verdade (a spec exige o arquivo começar com "WEBVTT").
      if (!body || !body.trimStart().startsWith('WEBVTT')) continue;

      await Filesystem.writeFile({
        path: `${dir}/${filename}`,
        directory: STORAGE_DIR,
        data: body,
        encoding: Encoding.UTF8
      });
      usedNames.add(filename);
      saved.push({
        sigla: legenda.sigla || '',
        nome: legenda.nome || legenda.sigla || 'Legenda',
        filename
      });
    } catch {
      // Legenda individual falhou: segue para as demais. Aula sem uma das
      // legendas > download inteiro marcado como problema.
    }
  }
  return saved;
}
